import { useEffect, useMemo, useRef, useState } from 'react';
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from 'date-fns';
import { listAppointments } from '../../api/appointments';
import { listMeetings } from '../../api/meetings/meetings';
import { getCompany } from '../../api/company/company';
import { listServices } from '../../api/companyRecords';
import { fetchCompanyStaff } from '../../shared/booking/service-staff/serviceStaffRouting';
import {
  normalizeCompanyAppointmentDoc,
  normalizeMeetingDoc,
  resolveAppointmentEmployeeId,
} from '../utils/normalizeCompanyAppointment';
import { formatStaffDisplayName } from '../utils/staffDisplayName';
import { MEETINGS_COLUMN_ID, UNASSIGNED_COLUMN_ID } from '../utils/columnMatch';

/** @param {'day' | 'week' | 'month'} view @param {'all' | 'appointments' | 'meetings'} itemTypeFilter */
export function useTeamLabelCalendarData({
  companyId,
  ownerId,
  user,
  selectedDate,
  view = 'day',
  itemTypeFilter = 'all',
  locationFilterId = null,
}) {
  const [rawAppointments, setRawAppointments] = useState([]);
  const [rawMeetings, setRawMeetings] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [companyHours, setCompanyHours] = useState(null);
  const [serviceColorsById, setServiceColorsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [staffLoading, setStaffLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedRef = useRef(false);

  const intervalBounds = useMemo(() => {
    if (!selectedDate) return null;
    if (view === 'day') {
      return { start: startOfDay(selectedDate), end: endOfDay(selectedDate) };
    }
    if (view === 'week') {
      return {
        start: startOfDay(startOfWeek(selectedDate, { weekStartsOn: 0 })),
        end: endOfDay(endOfWeek(selectedDate, { weekStartsOn: 0 })),
      };
    }
    const mStart = startOfMonth(selectedDate);
    const mEnd = endOfMonth(selectedDate);
    const calStart = startOfWeek(mStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(mEnd, { weekStartsOn: 0 });
    return {
      start: startOfDay(calStart),
      end: endOfDay(calEnd),
    };
  }, [view, selectedDate]);

  /** Bounds for the active view — narrower queries load less data in day/week mode. */
  const queryBounds = useMemo(() => {
    if (!selectedDate) return null;
    if (view === 'day') {
      return { start: startOfDay(selectedDate), end: endOfDay(selectedDate) };
    }
    if (view === 'week') {
      return {
        start: startOfDay(startOfWeek(selectedDate, { weekStartsOn: 0 })),
        end: endOfDay(endOfWeek(selectedDate, { weekStartsOn: 0 })),
      };
    }
    const mStart = startOfMonth(selectedDate);
    const mEnd = endOfMonth(selectedDate);
    const calStart = startOfWeek(mStart, { weekStartsOn: 0 });
    const calEnd = endOfWeek(mEnd, { weekStartsOn: 0 });
    return {
      start: startOfDay(calStart),
      end: endOfDay(calEnd),
    };
  }, [
    view,
    selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null,
    selectedDate ? format(selectedDate, 'yyyy-MM') : null,
  ]);

  const rangeStartMs = queryBounds?.start?.getTime() ?? null;
  const rangeEndMs = queryBounds?.end?.getTime() ?? null;

  useEffect(() => {
    hasLoadedRef.current = false;
    setLoading(true);
    setStaffLoading(true);
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;

    const loadHours = async () => {
      try {
        const company = await getCompany({ companyId });
        if (company?.settings?.companyHours) {
          setCompanyHours(company.settings.companyHours);
        } else {
          setCompanyHours(null);
        }
      } catch (e) {
        console.error(e);
        setCompanyHours(null);
      }
    };

    loadHours();
    return undefined;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) {
      setServiceColorsById({});
      return undefined;
    }

    let cancelled = false;
    const loadServices = async () => {
      try {
        const services = await listServices({ companyId });
        if (cancelled) return;
        const map = {};
        services.forEach((service) => {
          if (service._init) return;
          if (service.calendarColor) map[service.id] = service.calendarColor;
        });
        setServiceColorsById((prev) => {
          const prevKeys = Object.keys(prev);
          const nextKeys = Object.keys(map);
          if (
            prevKeys.length === nextKeys.length &&
            nextKeys.every((key) => prev[key] === map[key])
          ) {
            return prev;
          }
          return map;
        });
      } catch (err) {
        console.warn('[useTeamLabelCalendarData] Could not load service colors:', err);
      }
    };
    loadServices();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const attachServiceCalendarColor = (item) => {
    if (item?.type === 'meeting') return item;
    const serviceId = item.serviceId || item.service?.id;
    const calendarColor =
      (serviceId && serviceColorsById[serviceId]) || item.service?.calendarColor || null;
    if (!calendarColor) return item;
    return { ...item, serviceCalendarColor: calendarColor };
  };

  useEffect(() => {
    if (!companyId) {
      setStaffList([]);
      setStaffLoading(false);
      return undefined;
    }

    let cancelled = false;
    setStaffLoading(true);

    const loadStaff = async () => {
      try {
        const staff = await fetchCompanyStaff({ companyId, ownerId });
        if (!cancelled) {
          setStaffList(staff);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setStaffList([]);
        }
      } finally {
        if (!cancelled) {
          setStaffLoading(false);
        }
      }
    };

    loadStaff();
    return () => {
      cancelled = true;
    };
  }, [companyId, ownerId]);

  useEffect(() => {
    if (!companyId || rangeStartMs == null || rangeEndMs == null) {
      setRawAppointments([]);
      setRawMeetings([]);
      setLoading(false);
      return undefined;
    }

    if (!hasLoadedRef.current) {
      setLoading(true);
    }
    setError(null);

    const startDate = new Date(rangeStartMs);
    const endDate = new Date(rangeEndMs);

    const needAppointments = itemTypeFilter !== 'meetings';
    const needMeetings = itemTypeFilter !== 'appointments';

    let cancelled = false;
    let apptReceived = !needAppointments;
    let meetReceived = !needMeetings;

    const tryFinishLoading = () => {
      if (cancelled) return;
      if (apptReceived && meetReceived) {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };

    const onQueryError = (err, label) => {
      if (cancelled) return;
      setError(err.message || `Failed to load ${label}`);
      hasLoadedRef.current = true;
      setLoading(false);
    };

    let unsubAppt = () => {};
    let unsubMeet = () => {};

    if (needAppointments) {
      const loadAppointments = async () => {
        const result = await listAppointments({
          companyId,
          from: startDate,
          to: endDate,
        });
        if (cancelled) return;
        const rows = (result.appointments || [])
          .map((appointment) => normalizeCompanyAppointmentDoc(appointment.id, appointment))
          .filter((appointment) => appointment.dateTime && !appointment.archived);
        setRawAppointments(rows);
        apptReceived = true;
        tryFinishLoading();
      };
      loadAppointments().catch((err) => onQueryError(err, 'appointment'));
      const poll = setInterval(() => {
        loadAppointments().catch((err) => onQueryError(err, 'appointment'));
      }, 12000);
      unsubAppt = () => clearInterval(poll);
    } else {
      setRawAppointments([]);
    }

    if (needMeetings) {
      const loadMeetings = async () => {
        const rows = await listMeetings({
          companyId,
          from: startDate,
          to: endDate,
          archived: false,
        });
        if (cancelled) return;
        const mapped = rows
          .map((meeting) => normalizeMeetingDoc(meeting.id, meeting))
          .filter((meeting) => meeting.dateTime && !meeting.archived);
        setRawMeetings(mapped);
        meetReceived = true;
        tryFinishLoading();
      };
      loadMeetings().catch((err) => onQueryError(err, 'meeting'));
      const poll = setInterval(() => {
        loadMeetings().catch((err) => onQueryError(err, 'meeting'));
      }, 12000);
      unsubMeet = () => clearInterval(poll);
    } else {
      setRawMeetings([]);
    }

    tryFinishLoading();

    const loadingTimeout = setTimeout(() => {
      if (cancelled || (apptReceived && meetReceived)) return;
      console.warn('[useTeamLabelCalendarData] Calendar load timed out', {
        apptReceived,
        meetReceived,
        itemTypeFilter,
      });
      hasLoadedRef.current = true;
      setLoading(false);
      setError((prev) =>
        prev ||
        'Calendar is taking longer than expected to load. Try refreshing or switching views.'
      );
    }, 20000);

    return () => {
      cancelled = true;
      clearTimeout(loadingTimeout);
      unsubAppt();
      unsubMeet();
    };
  }, [companyId, rangeStartMs, rangeEndMs, itemTypeFilter, view]);

  const fetchBounds = queryBounds;

  const appointmentsOnlyInRange = useMemo(
    () =>
      rawAppointments.filter((a) => {
        if (locationFilterId && a.locationId && a.locationId !== locationFilterId) {
          return false;
        }
        if (fetchBounds) {
          const t = a.dateTime?.getTime?.();
          if (t == null || t < fetchBounds.start.getTime() || t > fetchBounds.end.getTime()) {
            return false;
          }
        }
        if (a.type === 'meeting') return false;
        const st = (a.status || '').toLowerCase();
        if (st === 'cancelled' || st === 'canceled') return false;
        return true;
      }),
    [rawAppointments, fetchBounds, locationFilterId]
  );

  const meetingsInRange = useMemo(
    () =>
      rawMeetings.filter((m) => {
        if (fetchBounds) {
          const t = m.dateTime?.getTime?.();
          if (t == null || t < fetchBounds.start.getTime() || t > fetchBounds.end.getTime()) {
            return false;
          }
        }
        const st = (m.status || '').toLowerCase();
        if (st === 'cancelled' || st === 'canceled') return false;
        return true;
      }),
    [rawMeetings, fetchBounds]
  );

  const displayItemsInRange = useMemo(() => {
    let items;
    if (itemTypeFilter === 'appointments') items = appointmentsOnlyInRange;
    else if (itemTypeFilter === 'meetings') items = meetingsInRange;
    else {
      items = [...appointmentsOnlyInRange, ...meetingsInRange].sort(
        (a, b) => a.dateTime - b.dateTime
      );
    }
    return items.map(attachServiceCalendarColor);
  }, [appointmentsOnlyInRange, meetingsInRange, itemTypeFilter, serviceColorsById]);

  const dayKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;

  const displayItemsForSelectedDay = useMemo(() => {
    if (!dayKey) return [];
    if (view === 'day') {
      let items;
      if (itemTypeFilter === 'appointments') items = appointmentsOnlyInRange;
      else if (itemTypeFilter === 'meetings') items = meetingsInRange;
      else {
        items = [...appointmentsOnlyInRange, ...meetingsInRange].sort(
          (a, b) => a.dateTime - b.dateTime
        );
      }
      return items.map(attachServiceCalendarColor);
    }
    return displayItemsInRange.filter((a) => format(a.dateTime, 'yyyy-MM-dd') === dayKey);
  }, [
    view,
    dayKey,
    appointmentsOnlyInRange,
    meetingsInRange,
    itemTypeFilter,
    serviceColorsById,
    displayItemsInRange,
  ]);

  const columns = useMemo(() => {
    if (itemTypeFilter === 'meetings') {
      return [{ id: MEETINGS_COLUMN_ID, name: 'Meetings' }];
    }

    const isEmployeeUser = user?.type === 'employee';
    const uid = user?.uid;

    let cols = [...staffList]
      .map((s) => ({
        id: s.id,
        name: formatStaffDisplayName(s),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));

    if (isEmployeeUser && uid) {
      cols = cols.filter((c) => c.id === uid);
    }

    const appointmentColumnSource =
      view === 'day' && dayKey
        ? appointmentsOnlyInRange.filter((a) => format(a.dateTime, 'yyyy-MM-dd') === dayKey)
        : appointmentsOnlyInRange;

    const ids = new Set(cols.map((c) => c.id));
    appointmentColumnSource.forEach((a) => {
      const eid = resolveAppointmentEmployeeId(a);
      if (eid && !ids.has(eid)) {
        ids.add(eid);
        cols.push({
          id: eid,
          name: a.employeeName || a.employee?.name || 'Staff',
        });
      }
    });

    const hasUnassigned = appointmentColumnSource.some((a) => !resolveAppointmentEmployeeId(a));
    if (hasUnassigned && !isEmployeeUser && !ids.has(UNASSIGNED_COLUMN_ID)) {
      cols.push({ id: UNASSIGNED_COLUMN_ID, name: 'Unassigned' });
    }

    if (itemTypeFilter === 'all' && meetingsInRange.length > 0) {
      cols.push({ id: MEETINGS_COLUMN_ID, name: 'Meetings' });
    }

    cols.sort((a, b) => {
      if (a.id === MEETINGS_COLUMN_ID) return 1;
      if (b.id === MEETINGS_COLUMN_ID) return -1;
      if (a.id === UNASSIGNED_COLUMN_ID) return 1;
      if (b.id === UNASSIGNED_COLUMN_ID) return -1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });

    if (isEmployeeUser && uid) {
      cols = cols.filter((c) => c.id === uid || c.id === MEETINGS_COLUMN_ID);
    }

    if (isEmployeeUser && uid && cols.length === 0) {
      cols = [
        {
          id: uid,
          name:
            user?.displayName ||
            [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
            'My schedule',
        },
      ];
    }

    return cols;
  }, [staffList, appointmentsOnlyInRange, meetingsInRange, user, itemTypeFilter, view, dayKey]);

  return {
    loading: loading || staffLoading,
    error,
    companyHours,
    staffList,
    appointmentsInRange: displayItemsInRange,
    appointmentsForSelectedDay: displayItemsForSelectedDay,
    columns,
    intervalBounds,
  };
}
